import { useTranslator } from '@bluebeam/react-i18n';
import { useAuth } from 'auth';
import FileUpload from 'components/hoc/FileUpload';
import Upload from 'components/Icons/Upload';
import BaseInput from 'components/inputs/BaseInput';
import { SelectOption } from 'components/inputs/BaseSelect';
import BaseTextarea from 'components/inputs/BaseTextarea';
import DateDropdown from 'components/inputs/dropdowns/DateDropdown';
import OrderedSetMultiSelect from 'components/inputs/dropdowns/OrderedSetMultiSelect';
import SingleSelect from 'components/inputs/dropdowns/SingleSelect';
import Label from 'components/inputs/Label';
import { IconButton } from 'components/lib';
import OverlaySection from 'components/Overlays/OverlaySection/OverlaySection';
import { imageMimeTypes } from 'constants/files';
import { useIsPendoEnabled } from 'hooks/useIsPendoEnabled';
import Immutable, { List } from 'immutable';
import React from 'react';
import { CreateRFI } from 'records/interactions/CreateRFI';
import { PageId } from 'records/Page';
import { ProjectDriveSelectedItem } from 'records/ProjectDriveSelectedItem';
import { IntegrationsSelectedItem } from 'records/IntegrationsSelectedItem';
import { ImpactCategories } from 'records/RFI';
import { RFIAttachment } from 'records/RFIAttachment';
import { RFIFile } from 'records/RFIFile';
import { StudioProjectFileData } from 'records/StudioProjectFile';
import { UserEmail } from 'records/User';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import InsertFilesButton from '../InsertFilesButton';
import RFIFilesList from '../RFIFilesList/RFIFilesList';
import { AttachmentTypeEnum } from '../ViewRFIOverlay/ViewRFIMachine/viewRfiMachineTypes';

interface OverlayLabelWithDraftProps {
  interaction: CreateRFI;
}

const OverlayLabelWithDraft: React.FC<OverlayLabelWithDraftProps> = ({
  interaction,
}: OverlayLabelWithDraftProps) => {
  const { translate } = useTranslator(['rfiPage']);

  return (
    <div className="row justify-between items-center w100">
      <div>{translate('rfiPage:officialQuestion')}</div>
      {interaction.fromDraft && !interaction.expanded ? (
        <div className="b-2 px3 py1 font-size-4 regular">
          {translate('rfiPage:draft')}
        </div>
      ) : null}
    </div>
  );
};

interface Props extends WithAppStoreProps {
  interaction: CreateRFI;
}

const OfficialQuestionSection = (props: Props) => {
  const { isPendoEnabled } = useIsPendoEnabled();
  const { userId } = useAuth();
  const handleRFIChange = (
    field: string,
    val: string | number | Immutable.List<UserEmail> | Immutable.Set<PageId>
  ): void => {
    const { interaction, actions } = props;

    const updatedInteraction = interaction.setIn(['rfi', field], val);

    actions.updateInteraction(updatedInteraction);
  };

  const handleAddFile = (additionalFiles: FileList): void => {
    const { interaction, appState, actions } = props;

    const newRfiFiles = [...Array.from(additionalFiles)].reduce(
      (rfiFiles, file) => {
        if (file.name) {
          const isImageMimeType = imageMimeTypes.has(file.type);

          return rfiFiles.push(
            new RFIFile({
              projectid: appState.selectedProject,
              file,
              category: isImageMimeType ? 'imageattachment' : 'document',
              type: isImageMimeType ? 'IMAGE' : 'FILE',
            })
          );
        }
        return rfiFiles;
      },
      List<RFIFile>()
    );

    const combinedFiles = interaction.files.concat(newRfiFiles);

    const updatedInteraction = interaction
      .set('files', combinedFiles)
      .setIn(['rfi', 'attachmentcount'], combinedFiles.size);

    actions.updateInteraction(updatedInteraction);
  };

  const handleRemoveAttachmment = (
    attachment:
      | RFIAttachment
      | File
      | string
      | ProjectDriveSelectedItem
      | IntegrationsSelectedItem
      | StudioProjectFileData,
    source: string
  ): void => {
    const { interaction, actions } = props;

    let updatedInteraction;
    switch (source) {
      case AttachmentTypeEnum.SAVED:
        updatedInteraction = interaction
          .update('removedAttachments', (ra) =>
            ra.push(attachment as RFIAttachment)
          )
          .update('attachments', (a) => a.filter((aa) => aa !== attachment))
          .updateIn(['rfi', 'attachmentcount'], (ac) => ac - 1);
        break;
      case AttachmentTypeEnum.DRAWING:
        updatedInteraction = interaction.updateIn(
          ['rfi', 'pageids'],
          (pageids) => pageids.remove(attachment)
        );
        break;
      case AttachmentTypeEnum.STUDIO:
        updatedInteraction = interaction.updateIn(
          ['studioItems'],
          (studioItems) =>
            studioItems.remove(
              studioItems.findIndex(
                (item: StudioProjectFileData) => item === attachment
              )
            )
        );
        break;
      case AttachmentTypeEnum.DRIVE:
        updatedInteraction = interaction.updateIn(
          ['projectDriveItems'],
          (projectDriveItems) =>
            projectDriveItems.remove(
              projectDriveItems.findIndex(
                (item: ProjectDriveSelectedItem) => item === attachment
              )
            )
        );
        break;
      case AttachmentTypeEnum.INTEGRATION:
        updatedInteraction = interaction.updateIn(
          ['integrationItems'],
          (integrationItem) =>
            integrationItem.remove(
              integrationItem.findIndex(
                (item: IntegrationsSelectedItem) => item === attachment
              )
            )
        );
        break;
      default:
        updatedInteraction = interaction.update('files', (files) =>
          files.filter((f) => f.file !== attachment)
        );
        break;
    }

    actions.updateInteraction(updatedInteraction);
  };

  const { interaction, appState, actions } = props;
  const {
    rfi,
    files,
    attachments,
    projectDriveItems,
    integrationItems,
    studioItems,
  } = interaction;
  const { projects, selectedProject, users, sheets } = appState;
  const currentUser = users.find((u) => userId === u.id);

  const project = projects.get(selectedProject);
  const { translate } = useTranslator(['rfiPage']);

  const impactCategories: ImpactCategories = {
    NONE: translate('rfiPage:none'),
    COST: translate('rfiPage:cost'),
    SCHEDULE: translate('rfiPage:schedule'),
    COST_AND_SCHEDULE: translate('rfiPage:costAndSchedule'),
  };

  const attachedSheetversions = sheets
    .valueSeq()
    .flatMap((s) => s.versions)
    .filter((sv) => interaction.rfi.pageids.has(sv.pageid))
    .toList();

  if (!project || !currentUser) {
    return null;
  }

  const assigneeOptions = users
    .valueSeq()
    .map((u) => ({ label: u.email, value: u.email }))
    .sortBy(({ label }) => label)
    .toArray();

  const distroListOptions = users
    .map((u) => u.email)
    .toOrderedSet()
    .sort();

  const impactCategoryOptions = Object.entries(impactCategories).map(
    ([value, label]) => ({ label, value })
  );

  return (
    <OverlaySection label={<OverlayLabelWithDraft interaction={interaction} />}>
      <div className="mb3">
        <Label label={translate('rfiPage:subject')} required>
          <div className="light row justify-end mr3">
            {rfi.title.length}/200
          </div>
        </Label>
        <BaseInput
          id="create-rfi-overlay-subject-input"
          className="w100"
          value={rfi.title}
          maxLength={200}
          onChange={({
            target: { value },
          }: React.ChangeEvent<HTMLInputElement>): void => {
            handleRFIChange('title', value);
          }}
        />
      </div>

      <div className="mb3">
        <Label label={translate('rfiPage:question')} required />
        <BaseTextarea
          id="create-rfi-overlay-question-textarea"
          className="w100"
          value={rfi.question}
          onChange={({
            target: { value },
          }: React.ChangeEvent<HTMLTextAreaElement>): void =>
            handleRFIChange('question', value)
          }
        />
      </div>

      <div className="mb3">
        <Label label={translate('rfiPage:assignee')} required />
        <SingleSelect<string>
          id="create-rfi-overlay-assignee-select"
          options={assigneeOptions}
          placeholder={translate('formLabels:selectWithDots')}
          value={
            rfi.assignees.size > 0
              ? {
                  label: rfi.assignees.first(),
                  value: rfi.assignees.first(),
                }
              : null
          }
          onChange={(selectedAssignee): void =>
            handleRFIChange(
              'assignees',
              Immutable.List([selectedAssignee.value])
            )
          }
        />
      </div>

      {/* Distribution List */}
      <div className="mb3">
        <Label label={translate('rfiPage:distributionList')} />
        <OrderedSetMultiSelect
          id="create-rfi-overlay-distro-list-select"
          options={distroListOptions}
          values={rfi.distributionlist}
          placeholder={translate('formLabels:selectWithDots')}
          onChange={(nextValues): void =>
            handleRFIChange('distributionlist', nextValues)
          }
        />
      </div>

      <div
        style={
          interaction.expanded
            ? {
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridColumnGap: '24px',
              }
            : {}
        }
      >
        <div className="mb3">
          <Label label={translate('rfiPage:dueDate')} />
          <DateDropdown
            id="create-rfi-overlay-duedate"
            date={rfi.duedate}
            minDate={new Date()}
            onChange={(date: Date): void => {
              handleRFIChange(
                'duedate',
                Math.floor(date?.getTime() / 1000) || 0
              );
            }}
          />
        </div>

        {/* Impact */}
        <div className="mb3">
          <Label label={translate('rfiPage:impact')} />
          <SingleSelect
            id="create-rfi-overlay-impact-select"
            options={impactCategoryOptions}
            value={{
              label: impactCategories[rfi.impactcategory],
              value: rfi.impactcategory,
            }}
            onChange={(nextImpactCategory: SelectOption) =>
              handleRFIChange('impactcategory', nextImpactCategory.value)
            }
          />
        </div>
      </div>
      <RFIFilesList
        files={files}
        sheetVersions={attachedSheetversions}
        attachments={attachments}
        projectDriveItems={projectDriveItems}
        integrationItems={integrationItems}
        studioItems={studioItems}
        onAttachmentRemove={(attachment, source) =>
          handleRemoveAttachmment(attachment, source)
        }
      />
      <div className="w100 row justify-between">
        <InsertFilesButton
          id="create-rfi-overlay-insert-files-btn"
          initialPages={rfi.pageids}
          initialProjectDriveItems={projectDriveItems}
          initialIntegrationItems={integrationItems}
          initialStudioItems={studioItems}
          onSubmit={({
            selectedPages,
            projectDriveSelectedItems,
            integrationsSelectedItems,
            studioItems: studioSelectedItems,
            studioProjectData,
          }) => {
            const updatedInteraction = interaction
              .setIn(['rfi', 'pageids'], selectedPages)
              .setIn(['projectDriveItems'], projectDriveSelectedItems)
              .setIn(['integrationItems'], integrationsSelectedItems)
              .setIn(['studioItems'], studioSelectedItems)
              .setIn(['studioData'], studioProjectData);
            actions.updateInteraction(updatedInteraction);
            if (isPendoEnabled && projectDriveSelectedItems.count() > 0) {
              pendo.track('Added RFI Attachment from Integrations');
            }
            if (isPendoEnabled && integrationsSelectedItems.count() > 0) {
              pendo.track('Added RFI Attachment from Account Integrations');
            }
          }}
          className="grow-1 ws2"
        />
        <div className="ws1" />
        <FileUpload
          id="create-rfi-overlay-attach-document"
          accept=""
          onFileSelect={(addFiles): void => handleAddFile(addFiles)}
        >
          {({ openFilePicker }): React.ReactNode => (
            <IconButton
              variant="secondary"
              isLoading={false}
              size="large"
              onClick={openFilePicker}
              className="flex-center grow-1 ws2"
            >
              <Upload />
              {translate('buttons:uploadFiles')}
            </IconButton>
          )}
        </FileUpload>
      </div>
    </OverlaySection>
  );
};

export default withAppStore<Props>(OfficialQuestionSection);
